import posthog from 'posthog-js';
import { syncOneSignalIdentity } from '@/components/entities/analytics/components/OneSIgnal/utils/oneSignal.client';
import { gtmUserProfileStore } from '@/components/entities/analytics/gtm';
import { authApi } from '@/queries/auth/auth.api';
import { tokenManager } from '@/queries/token-manager';
import { checkToken } from '@/redux/slices/userSlice';
import { store } from '@/redux/store';

type LogoutRedirectHandler = (url: string) => void;

type LogoutUserOptions = {
  redirectHomePage?: boolean;
  logoutAll?: boolean;
  redirectUrl?: string;
  onRedirect?: LogoutRedirectHandler;
};

const resolveLogoutOptions = (
  redirectHomePageOrOptions: boolean | LogoutUserOptions = false,
  logoutAll = false,
  redirectUrl?: string,
  onRedirect?: LogoutRedirectHandler,
): Required<Pick<LogoutUserOptions, 'redirectHomePage' | 'logoutAll'>> & Pick<LogoutUserOptions, 'redirectUrl' | 'onRedirect'> => {
  if (typeof redirectHomePageOrOptions === 'object') {
    return {
      redirectHomePage: redirectHomePageOrOptions.redirectHomePage ?? false,
      logoutAll: redirectHomePageOrOptions.logoutAll ?? false,
      redirectUrl: redirectHomePageOrOptions.redirectUrl,
      onRedirect: redirectHomePageOrOptions.onRedirect,
    };
  }

  return {
    redirectHomePage: redirectHomePageOrOptions,
    logoutAll,
    redirectUrl,
    onRedirect,
  };
};

export const logoutUser = async (
  redirectHomePageOrOptions: boolean | LogoutUserOptions = false,
  logoutAll = false,
  redirectUrl?: string,
  onRedirect?: LogoutRedirectHandler,
) => {
  const {
    redirectHomePage,
    logoutAll: shouldLogoutAll,
    redirectUrl: targetRedirectUrl,
    onRedirect: handleRedirect,
  } = resolveLogoutOptions(redirectHomePageOrOptions, logoutAll, redirectUrl, onRedirect);

  const redirectAfterLogout = () => {
    const targetUrl = targetRedirectUrl ?? (redirectHomePage ? '/' : '/?m=login');

    if (handleRedirect) {
      handleRedirect(targetUrl);
      return;
    }

    window.location.href = targetUrl;
  };

  const clearOneSignalIdentity = () => {
    void syncOneSignalIdentity(undefined).catch(() => {
      // ignore OneSignal logout errors during user logout flow
    });
  };

  try {
    if (shouldLogoutAll) {
      await authApi.logoutAll();
    } else {
      await authApi.logout();
    }

    gtmUserProfileStore.clear();
    tokenManager.removeTokens();
    localStorage.removeItem('currency');
    store().dispatch(checkToken());
    posthog.reset();
    clearOneSignalIdentity();

    window.cioanalytics?.identify(tokenManager.getUserId() || '', { subscription_status: false });

    redirectAfterLogout();
  } catch (error) {
    console.error('Logout failed', error);

    gtmUserProfileStore.clear();
    tokenManager.removeTokens();
    store().dispatch(checkToken());
    clearOneSignalIdentity();

    // Ensure we still navigate even if API logout fails
    redirectAfterLogout();
  }
};
